import React, { useState, useEffect, useCallback } from "react";
import style from "./Carta.module.css";
import { 
  listarVinosByCategoria,
  listarTiposCategoriaByCategoria,
  listarVinosByTipoCategoria,
  obtenerFavoritos,
  agregarFavorito,
  eliminarFavorito,
  compartirPedido
} from "../../repositorios/apiServer";
import useStore from "../../useStore";
import { FaStar, FaShareAlt } from "react-icons/fa";
import SwipeContainer from "./SwipeContainer";

function throttle(func, limit) {
  let inThrottle;
  return function() {
    const args = arguments;
    const context = this;
    if (!inThrottle) {
      func.apply(context, args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  };
}

const VinosLista = ({ tipoCategoria, onVinoClick, onSeccionVisible, onSinProductos, loadFavoritos = false }) => {
  const [vinosPorTipo, setVinosPorTipo] = useState({});
  const [tiposCategorias, setTiposCategorias] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);
  const [favoritos, setFavoritos] = useState([]);
  const [compartiendo, setCompartiendo] = useState(null);
  const [favoritosCargados, setFavoritosCargados] = useState(false);
  const userToken = useStore((state) => state.userToken);
  
  useEffect(() => {
    if (!cargando && (error || Object.keys(vinosPorTipo).length === 0)) {
      if (onSinProductos) {
        onSinProductos();
      }
    }
  }, [cargando, error, vinosPorTipo, onSinProductos]);
  
  useEffect(() => {
    const cargarFavoritos = async () => {
      if (!userToken || favoritosCargados || !loadFavoritos) {
        return;
      }
      
      try {
        const favoritosData = await obtenerFavoritos(userToken);
        if (Array.isArray(favoritosData)) {
          const favIds = favoritosData.map(fav => {
            const id = fav._id || fav.id;
            return id ? id.toString() : null;
          }).filter(id => id !== null);
          
          setFavoritos(favIds);
        }
        setFavoritosCargados(true);
      } catch (error) {
        console.error("Error al cargar favoritos:", error);
      }
    };

    cargarFavoritos();
  }, [userToken, loadFavoritos, favoritosCargados]);

  useEffect(() => {
    let isMounted = true;
    
    if (!tipoCategoria) {
      setVinosPorTipo({});
      setTiposCategorias([]);
      setCargando(false);
      return;
    }

    const cargarTodosLosVinos = async () => {
      setCargando(true);
      setError(null);
      
      try {
        const tiposObtenidos = await listarTiposCategoriaByCategoria(tipoCategoria);
        
        if (!isMounted) return;
        
        if (!tiposObtenidos || !Array.isArray(tiposObtenidos) || tiposObtenidos.length === 0) {
          setCargando(false);
          setError("No se encontraron tipos de vino para esta categoría");
          return;
        }
        
        setTiposCategorias(tiposObtenidos);

        const requests = tiposObtenidos.map(async (tipo) => {
          const tipoId = tipo._id || tipo.id;
          const tipoNombre = tipo.nombre;
          
          try {
            const vinosDeTipo = await listarVinosByTipoCategoria(tipoId);
            
            if (vinosDeTipo && Array.isArray(vinosDeTipo) && vinosDeTipo.length > 0) {
              const vinosConMarca = vinosDeTipo.map(vino => ({
                ...vino,
                esVino: true
              }));
              
              return {
                tipoId,
                data: {
                  nombre: tipoNombre,
                  vinos: vinosConMarca
                }
              };
            } else {
              return {
                tipoId,
                data: {
                  nombre: tipoNombre,
                  vinos: []
                }
              };
            }
          } catch (errorTipo) {
            console.error(`Error al obtener vinos para el tipo "${tipoNombre}":`, errorTipo);
            return {
              tipoId,
              data: {
                nombre: tipoNombre,
                vinos: [],
                error: true
              }
            };
          }
        });
        
        const results = await Promise.all(requests);
        
        if (!isMounted) return;
        
        const resultadosPorTipo = {};
        results.forEach(result => {
          resultadosPorTipo[result.tipoId] = result.data;
        });
        
        setVinosPorTipo(resultadosPorTipo);
        
        const totalVinos = Object.values(resultadosPorTipo)
          .reduce((total, grupo) => total + grupo.vinos.length, 0);
        
        if (totalVinos === 0) {
          setError("No se encontraron vinos para esta categoría");
        } else {
          setError(null);
        }
        
      } catch (errorGeneral) {
        console.error("Error al cargar tipos de categorías y vinos:", errorGeneral);
        if (isMounted) {
          setError("Ocurrió un error al cargar los vinos. Inténtalo de nuevo.");
        }
      } finally {
        if (isMounted) {
          setCargando(false);
        }
      }
    };

    cargarTodosLosVinos();
    
    return () => {
      isMounted = false;
    };
  }, [tipoCategoria]);

  useEffect(() => {
    if (!tiposCategorias || tiposCategorias.length === 0 || typeof onSeccionVisible !== 'function') {
      return;
    }
    
    const mainContainer = document.querySelector(`.${style.tipos_vinos_container}`);
    if (!mainContainer) {
      console.error("No se encontró el contenedor principal de vinos");
      return;
    }
    
    const options = {
      root: null,
      rootMargin: '-10% 0px -70% 0px',
      threshold: 0.1
    };
    
    const observer = new IntersectionObserver((entries) => {
      const visibleEntries = entries
        .filter(entry => entry.isIntersecting)
        .sort((a, b) => {
          return Math.abs(a.boundingClientRect.top) - Math.abs(b.boundingClientRect.top);
        });
      
      if (visibleEntries.length > 0) {
        const firstVisible = visibleEntries[0];
        
        const seccionPadre = firstVisible.target.closest(`.${style.tipo_vinos_grupo}`);
        
        if (seccionPadre) {
          const seccionesNodeList = document.querySelectorAll(`.${style.tipo_vinos_grupo}`);
          const secciones = Array.from(seccionesNodeList);
          const indice = secciones.indexOf(seccionPadre);
          
          if (indice !== -1) {
            if (tiposCategorias && tiposCategorias.length > indice) {
              const categoria = tiposCategorias[indice];
              onSeccionVisible(indice, categoria);
            }
          }
        }
      }
    }, options);
    
    const titulos = document.querySelectorAll(`.${style.tipo_vinos_titulo}`);
    
    titulos.forEach(titulo => {
      observer.observe(titulo);
    });
    
    setTimeout(() => {
      if (tiposCategorias.length > 0) {
        const scrollOffset = 150;
        const elementoVisible = document.elementFromPoint(window.innerWidth / 2, scrollOffset);
        
        let seccionVisible = null;
        if (elementoVisible) {
          let seccionPadre = elementoVisible;
          while (seccionPadre && !seccionPadre.className.includes(style.tipo_vinos_grupo)) {
            seccionPadre = seccionPadre.parentElement;
            if (!seccionPadre) break;
          }
          
          if (seccionPadre) {
            const seccionesArray = Array.from(document.querySelectorAll(`.${style.tipo_vinos_grupo}`));
            const indice = seccionesArray.indexOf(seccionPadre);
            
            if (indice !== -1) {
              onSeccionVisible(indice, tiposCategorias[indice]);
              seccionVisible = true;
            }
          }
        }
        
        if (!seccionVisible) {
          onSeccionVisible(0, tiposCategorias[0]);
        }
      }
    }, 10);
    
    return () => {
      observer.disconnect();
    };
  }, [tiposCategorias, onSeccionVisible, style]);

  const getId = (vino) => {
    return (vino._id?.$oid || vino._id || vino.id || Math.random().toString()).toString();
  };

  const formatearPrecio = (precio) => {
    if (precio === undefined || precio === null) return "--";
    return parseFloat(precio).toFixed(2) + "€";
  };

  const handleClick = (e, vino) => {
    e.preventDefault();
    
    if (onVinoClick) {
      onVinoClick({
        ...vino,
        esVino: true
      });
    }
  };

  const handleFavorito = useCallback(throttle(async (e, vino) => {
    e.stopPropagation();
    e.preventDefault();
    
    if (!userToken) {
      alert("Debes iniciar sesión para añadir favoritos");
      return;
    }
    
    const vinoId = getId(vino);
    
    try {
      const esFavorito = favoritos.includes(vinoId);
      
      const boton = e.currentTarget;
      if (esFavorito) {
        boton.classList.remove(style.active);
        setFavoritos(favoritos.filter(id => id !== vinoId));
      } else {
        boton.classList.add(style.active);
        setFavoritos([...favoritos, vinoId]);
      }
      
      if (esFavorito) {
        await eliminarFavorito(userToken, vinoId);
      } else {
        await agregarFavorito(userToken, vinoId, true);
      }
    } catch (error) {
      console.error("Error al gestionar favorito para vino:", error);
      alert("Error al gestionar favorito. Por favor, inténtalo de nuevo.");
    }
  }, 300), [userToken, favoritos]);

  const compartirVino = useCallback(throttle(async (e, vino) => {
    e.preventDefault();
    e.stopPropagation();
    
    const vinoId = getId(vino);

    if (compartiendo === vinoId) {
      return;
    }
    
    if (!userToken) {
      alert("Debes iniciar sesión para compartir vinos");
      return;
    }
    
    if (!vino) return;
    
    try {
      setCompartiendo(vinoId);
      
      const datosCompartir = {
        pedidoId: vinoId,
        usuarioId: userToken,
        nombreUsuario: "Usuario",
        productos: [
          {
            _id: vinoId,
            nombre: vino.nombre || "Vino sin nombre",
            precio: vino.precio || 0,
            cantidad: 1,
            esVino: true
          }
        ],
        fecha: new Date().toISOString()
      };

      const recomendacionCreada = await compartirPedido(datosCompartir);
      
      if (!recomendacionCreada || !recomendacionCreada._id) {
        throw new Error("No se pudo crear la recomendación");
      }

      const baseUrl = window.location.origin;
      const recomendacionId = recomendacionCreada._id;
      const enlaceCompartir = `${baseUrl}/recomendaciones/compartido/${recomendacionId}`;

      const mensajeCompartir = 
        `¡Mira este vino que te recomiendo en Romana! 🍷\n\n` +
        `${vino.nombre || "Vino recomendado"}\n\n` +
        `👉 ${enlaceCompartir}`;

      const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(mensajeCompartir)}`;
      window.open(whatsappUrl, '_blank');

      try {
        await navigator.clipboard.writeText(enlaceCompartir);
        alert(`Enlace copiado al portapapeles. Se ha abierto WhatsApp para compartir.`);
      } catch (clipboardError) {
        console.error("Error al copiar al portapapeles:", clipboardError);
        alert(`Se ha abierto WhatsApp. El enlace para compartir es: ${enlaceCompartir}`);
      }
    } catch (error) {
      console.error("Error al compartir vino:", error);
      alert("Error al compartir el vino. Por favor, inténtalo de nuevo.");
    } finally {
      setTimeout(() => {
        setCompartiendo(null);
      }, 1000);
    }
  }, 300), [userToken, compartiendo]);

  return (
    <>
      {cargando ? (
        <div className={style.loading_message}>Cargando vinos...</div>
      ) : error ? (
        null
      ) : (
        <div className={style.tipos_vinos_container}>
          {tiposCategorias.map((tipo) => {
            const tipoId = tipo._id || tipo.id;
            const grupoVinos = vinosPorTipo[tipoId];
            
            if (!grupoVinos || grupoVinos.vinos.length === 0) {
              return null;
            }
            
            return (
              <div key={tipoId} className={style.tipo_vinos_grupo}>
                <h2 className={style.tipo_vinos_titulo}>{tipo.nombre}</h2>
                <div className={style.productos_lista}>
                  {grupoVinos.vinos.map((vino) => {
                    const vinoId = getId(vino);
                    const esFavorito = favoritos.includes(vinoId);
                    const estaCompartiendo = compartiendo === vinoId;
                    
                    return (
                      <div 
                        className={style.favorito_container}
                        key={vinoId}
                        onClick={(e) => handleClick(e, vino)}
                      >
                        <SwipeContainer className={style.producto_nombre_precio}>
                          <div className={style.nombre_con_estrella}>
                            <h3 className={style.producto_nombre}>{vino.nombre || "Vino sin nombre"}</h3>
                            <div className={style.iconos_producto}>
                              <button 
                                className={`${style.boton_compartir_producto} ${estaCompartiendo ? style.compartiendo : ''}`}
                                onClick={(e) => compartirVino(e, vino)}
                                title="Compartir este vino"
                                aria-label="Compartir vino a recomendaciones"
                                disabled={estaCompartiendo}
                              >
                                <FaShareAlt size={14} />
                              </button>
                              <button 
                                className={`${style.boton_favorito} ${esFavorito ? style.active : ''}`}
                                onClick={(e) => handleFavorito(e, vino)}
                                title={esFavorito ? "Quitar de favoritos" : "Añadir a favoritos"}
                                aria-label={esFavorito ? "Quitar de favoritos" : "Añadir a favoritos"}
                              >
                                <FaStar size={16} />
                              </button>
                            </div>
                          </div>
                          {vino.precio !== undefined && vino.precio !== null && (
                            <p className={style.producto_precio}>{formatearPrecio(vino.precio)}</p>
                          )}
                          <div className={style.swipe_indicator}></div>
                        </SwipeContainer>
                        
                        {vino.descripcion && vino.descripcion !== "" && (
                          <p className={style.producto_descripcion}>{vino.descripcion}</p>
                        )}
                        
                        <div className={style.vino_informacion_adicional}>
                          {vino.bodega && (
                            <p className={style.vino_detalle}>
                              <strong>Bodega:</strong> {vino.bodega}
                            </p>
                          )}
                          {vino.anio && (
                            <p className={style.vino_detalle}>
                              <strong>Año:</strong> {vino.anio}
                            </p>
                          )}
                          {vino.do && (
                            <p className={style.vino_detalle}>
                              <strong>D.O.:</strong> {vino.do}
                            </p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
};

export default VinosLista;